import { getGsbTenantCode, setGsbTenantCode, getLocalStorage, removeLocalStorage, setLocalStorage, } from "../../config/gsb-config.js";
function isCacheEntry(value) {
    return (typeof value === "object" &&
        value !== null &&
        "data" in value &&
        "timestamp" in value &&
        typeof value.timestamp === "number");
}
export class GenericCacheService {
    genericCache = new Map();
    CACHE_DURATION = Number.MAX_SAFE_INTEGER;
    dbPromise = null;
    DB_NAME = "gsb-cache-db";
    OBJECT_STORE_NAME = "gsb-cache-store";
    removeLegacyMetadataStorage() {
        if (typeof window === "undefined")
            return;
        for (const key of Object.keys(globalThis.localStorage)) {
            if (key.startsWith("gsb_entityDef_") ||
                key.startsWith("gsb_enum_") ||
                key.startsWith("gsb_propDef_")) {
                globalThis.localStorage?.removeItem(key);
            }
        }
    }
    isIndexedDbSupported() {
        return typeof window !== "undefined" && "indexedDB" in window;
    }
    getDb() {
        if (!this.isIndexedDbSupported()) {
            return Promise.reject(new Error("IndexedDB is not supported in this environment."));
        }
        if (this.dbPromise) {
            return this.dbPromise;
        }
        this.dbPromise = new Promise((resolve, reject) => {
            const openRequest = indexedDB.open(this.DB_NAME, 1);
            openRequest.onupgradeneeded = () => {
                if (!openRequest.result.objectStoreNames.contains(this.OBJECT_STORE_NAME)) {
                    openRequest.result.createObjectStore(this.OBJECT_STORE_NAME);
                }
            };
            openRequest.onsuccess = () => {
                resolve(openRequest.result);
            };
            openRequest.onerror = () => {
                this.dbPromise = null; // Reset promise on error
                reject(openRequest.error ?? new Error("Failed to open IndexedDB"));
            };
        });
        return this.dbPromise;
    }
    async getIndexedDb(key) {
        const db = await this.getDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.OBJECT_STORE_NAME, "readonly");
            const store = transaction.objectStore(this.OBJECT_STORE_NAME);
            const request = store.get(key);
            request.onsuccess = () => {
                const result = request.result;
                resolve(isCacheEntry(result) ? result : null);
            };
            request.onerror = () => {
                reject(request.error ?? new Error("Failed to read IndexedDB cache"));
            };
        });
    }
    async setIndexedDb(key, value) {
        const db = await this.getDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.OBJECT_STORE_NAME, "readwrite");
            const store = transaction.objectStore(this.OBJECT_STORE_NAME);
            const entry = { data: value, timestamp: Date.now() };
            const request = store.put(entry, key);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error ?? new Error("Failed to write IndexedDB cache"));
        });
    }
    async removeIndexedDb(key) {
        const db = await this.getDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.OBJECT_STORE_NAME, "readwrite");
            const store = transaction.objectStore(this.OBJECT_STORE_NAME);
            const request = store.delete(key);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error ?? new Error("Failed to remove IndexedDB cache"));
        });
    }
    async clearIndexedDb() {
        if (!this.isIndexedDbSupported()) {
            return;
        }
        try {
            const db = await this.getDb();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(this.OBJECT_STORE_NAME, "readwrite");
                const store = transaction.objectStore(this.OBJECT_STORE_NAME);
                const request = store.clear();
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error ?? new Error("Failed to clear IndexedDB cache"));
            });
        }
        catch (error) {
            console.error("Error clearing IndexedDB:", error);
        }
    }
    getCache(key, localStorageKey, duration) {
        const cacheTime = duration || this.CACHE_DURATION;
        // Check memory cache first
        const cached = this.genericCache.get(key);
        if (cached && Date.now() - cached.timestamp < cacheTime) {
            return cached.data;
        }
        // If not in memory cache but localStorage key is provided, try there
        if (localStorageKey && typeof window !== "undefined") {
            try {
                // Use the getLocalStorage function from gsb-config
                const storedValue = getLocalStorage(localStorageKey);
                if (storedValue) {
                    try {
                        // Try to parse it as JSON
                        const parsedData = JSON.parse(storedValue);
                        // Store back in memory cache and return the data
                        this.genericCache.set(key, {
                            data: parsedData,
                            timestamp: Date.now(),
                        });
                        return parsedData;
                    }
                    catch {
                        // If we can't parse as JSON, use it as a primitive value
                        this.genericCache.set(key, {
                            data: storedValue,
                            timestamp: Date.now(),
                        });
                        return storedValue;
                    }
                }
            }
            catch (error) {
                console.error(`Error retrieving data from localStorage with key ${localStorageKey}:`, error);
            }
        }
        return null;
    }
    setCache(key, value, duration, localStorageKey) {
        const cacheTime = duration || this.CACHE_DURATION;
        // Store in memory cache
        this.genericCache.set(key, {
            data: value,
            timestamp: Date.now(),
        });
        // If localStorage key is provided, also store there
        if (localStorageKey && typeof window !== "undefined") {
            try {
                if (typeof value === "string") {
                    // Use the setLocalStorage function from gsb-config for string values
                    setLocalStorage(localStorageKey, value, cacheTime);
                }
                else {
                    // For non-string values, stringify them first
                    setLocalStorage(localStorageKey, JSON.stringify(value), cacheTime);
                }
            }
            catch (error) {
                console.error(`Error storing data in localStorage with key ${localStorageKey}:`, error);
            }
        }
        return value;
    }
    removeCache(key, localStorageKey) {
        this.genericCache.delete(key);
        if (localStorageKey && typeof window !== "undefined") {
            try {
                removeLocalStorage(localStorageKey);
            }
            catch (error) {
                console.error(`Error removing data from localStorage with key ${localStorageKey}:`, error);
            }
        }
    }
    async getCacheAsync(key, persistenceKey, duration, persistence = "localStorage") {
        const cacheTime = duration || this.CACHE_DURATION;
        // Check memory cache first
        const cached = this.genericCache.get(key);
        if (cached && Date.now() - cached.timestamp < cacheTime) {
            return cached.data;
        }
        // If not in memory cache but persistence key is provided, try there
        if (persistenceKey && typeof window !== "undefined") {
            try {
                if (persistence === "localStorage") {
                    const storedValue = getLocalStorage(persistenceKey);
                    if (storedValue) {
                        try {
                            const parsedData = JSON.parse(storedValue);
                            this.genericCache.set(key, {
                                data: parsedData,
                                timestamp: Date.now(),
                            });
                            return parsedData;
                        }
                        catch {
                            this.genericCache.set(key, {
                                data: storedValue,
                                timestamp: Date.now(),
                            });
                            return storedValue;
                        }
                    }
                }
                else if (persistence === "indexedDB" && this.isIndexedDbSupported()) {
                    const entry = await this.getIndexedDb(persistenceKey);
                    if (entry && Date.now() - entry.timestamp < cacheTime) {
                        this.genericCache.set(key, {
                            data: entry.data,
                            timestamp: Date.now(),
                        });
                        return entry.data;
                    }
                    if (entry) {
                        // expired
                        await this.removeIndexedDb(persistenceKey);
                    }
                }
            }
            catch (error) {
                console.error(`Error retrieving data with persistence '${persistence}' and key ${persistenceKey}:`, error);
            }
        }
        return null;
    }
    async setCacheAsync(key, value, duration, persistenceKey, persistence = "localStorage") {
        // Store in memory cache
        this.genericCache.set(key, {
            data: value,
            timestamp: Date.now(),
        });
        // If persistence key is provided, also store there
        if (persistenceKey && typeof window !== "undefined") {
            try {
                if (persistence === "localStorage") {
                    const cacheTime = duration || this.CACHE_DURATION;
                    if (typeof value === "string") {
                        setLocalStorage(persistenceKey, value, cacheTime);
                    }
                    else {
                        setLocalStorage(persistenceKey, JSON.stringify(value), cacheTime);
                    }
                }
                else if (persistence === "indexedDB" && this.isIndexedDbSupported()) {
                    await this.setIndexedDb(persistenceKey, value);
                }
            }
            catch (error) {
                console.error(`Error storing data with persistence '${persistence}' and key ${persistenceKey}:`, error);
            }
        }
        return value;
    }
    async removeCacheAsync(key, persistenceKey, persistence = "localStorage") {
        this.genericCache.delete(key);
        if (persistenceKey && typeof window !== "undefined") {
            try {
                if (persistence === "localStorage") {
                    removeLocalStorage(persistenceKey);
                }
                else if (persistence === "indexedDB" && this.isIndexedDbSupported()) {
                    await this.removeIndexedDb(persistenceKey);
                }
            }
            catch (error) {
                console.error(`Error removing data from with persistence '${persistence}' and key ${persistenceKey}:`, error);
            }
        }
    }
    constructor(skipInitialization = false) {
        if (!skipInitialization) {
            this.removeLegacyMetadataStorage();
            const currentTenant = getGsbTenantCode();
            if (currentTenant)
                setGsbTenantCode(currentTenant);
        }
    }
    adoptGenericState(cache) {
        for (const [key, entry] of cache.genericCache)
            this.genericCache.set(key, entry);
        cache.genericCache = this.genericCache;
    }
    async clearCache() {
        this.genericCache.clear();
        if (typeof window !== "undefined") {
            try {
                this.removeLegacyMetadataStorage();
            }
            catch (error) {
                console.error("Error clearing localStorage cache:", error);
            }
        }
        await this.clearIndexedDb();
    }
}
//# sourceMappingURL=generic-cache.service.js.map